import {
  ANALYZER_CONNECTION_LABELS,
  ANALYZER_PROTOCOL_LABELS,
  type AnalyzerConnectionState,
  type AnalyzerView,
} from '@wodi/shared';
import { Tag, TextField, type TagState } from '@wodi/ui';

import { formatInstant } from '../patients/format.ts';

// What every screen says about a lab analyzer's connection (lab connector plan §4.4, §6):
// a word on a neutral tag, never a colour alone.

const TAGS: Record<AnalyzerConnectionState, TagState> = {
  connected: 'active',
  analyzer_disconnected: 'attention',
  connector_offline: 'attention',
  never_seen: 'neutral',
};

/** What to do next for each state that isn't connected. */
export const CONNECTION_NEXT_STEP: Record<AnalyzerConnectionState, string | null> = {
  connected: null,
  analyzer_disconnected:
    'The connector is running but has no link to the analyzer. Check the cable or network and the analyzer’s host settings.',
  connector_offline:
    'Wodi has not heard from the connector for over 3 minutes. Check the lab computer is on and connected. Results wait on it meanwhile.',
  never_seen:
    'Install the connector on the lab computer with this analyzer’s credential. It shows as connected once it starts.',
};

export function ConnectionTag({ analyzer }: { analyzer: AnalyzerView }) {
  if (analyzer.status === 'retired') return <Tag state="neutral">Retired</Tag>;
  const state = analyzer.connection.state;
  return <Tag state={TAGS[state]}>{ANALYZER_CONNECTION_LABELS[state]}</Tag>;
}

export function lastSeenText(analyzer: AnalyzerView): string {
  return analyzer.connection.lastSeenAt === null
    ? 'Not seen yet'
    : `Last seen ${formatInstant(analyzer.connection.lastSeenAt)}`;
}

export function protocolText(analyzer: AnalyzerView): string {
  return ANALYZER_PROTOCOL_LABELS[analyzer.protocol];
}

/** The credential, shown once, with what to do with it. */
export function CredentialShownOnce({ credential }: { credential: string }) {
  return (
    <>
      <p>
        Install this credential on the lab computer as the connector&apos;s
        WODI_ANALYZER_CREDENTIAL. It is shown only now. If it is lost, rotate it and install the new
        one.
      </p>
      <TextField label="Analyzer credential" value={credential} isReadOnly />
    </>
  );
}
